'use client';

import { useEffect } from 'react';

import { isChunkLoadError } from './chunk-load-error';

export { isChunkLoadError };

/**
 * Recover a tab that is still running the previous deploy.
 *
 * Next.js splits the app into hashed chunks and fetches them on demand. Deploy
 * a new build and the old hashes stop existing, so a tab that has been open
 * across the deploy asks for a file that is no longer there the moment it
 * navigates to a route it has not loaded yet. The import rejects with
 * `ChunkLoadError` and the user gets an error page for what is, in truth, a
 * stale tab. Everything they need is on the server; they just have the old
 * index.
 *
 * Reloading fetches the new index and the error disappears. The catch is that
 * reloading is also what a broken deploy would do forever, so this reloads
 * **once** per tab: the flag is set before the reload, and a second
 * ChunkLoadError with the flag already set falls through to the error page
 * where the person can at least see what happened.
 */
const RELOAD_FLAG = 'chunk-reload-attempted';

/*
 * True once this document has rendered a chunk-load error. The root layout
 * clears the one-reload flag on mount, and that layout stays up around
 * `error.tsx` and is mounted again inside `global-error.tsx`. Those two
 * trees run effects in opposite orders, so the clear waits one turn and
 * skips when a recovery effect has already run. The flag lives in memory:
 * a reload starts a new document, which is what makes the next deploy
 * eligible for its own retry.
 */
let chunkLoadRecoveryActive = false;

export function useRecoverFromStaleDeploy(error: unknown) {
  useEffect(() => {
    if (!isChunkLoadError(error)) return;
    if (typeof window === 'undefined') return;

    chunkLoadRecoveryActive = true;

    let alreadyTried = false;

    try {
      alreadyTried = window.sessionStorage.getItem(RELOAD_FLAG) === '1';
      window.sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch {
      /*
       * Private mode, or storage disabled. Without somewhere to record the
       * attempt there is no way to stop a loop, so do not start one.
       */
      return;
    }

    if (alreadyTried) return;

    window.location.reload();
  }, [error]);
}

/**
 * Clear the guard once the app is running normally again, so the *next* deploy
 * gets its own single retry rather than inheriting a spent one.
 */
export function useClearStaleDeployGuard() {
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (chunkLoadRecoveryActive) return;

      try {
        window.sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        /* Nothing to clear. */
      }
    }, 0);

    return () => window.clearTimeout(timeout);
  }, []);
}
